import { Media, Skeleton, Spacer } from '@syconium/little-miss-figgy'

import { Body as FitModelCaption } from '../../../../../brunswick/components/fit-model/FitModelCaption/styles'
import { PageWidthContainer } from '../../../../../lib/shared-styles'
import { CarouselImageSectionSkeleton } from '../CarouselImagesSection/Skeleton'
import { DetailsSectionSkeleton } from '../DetailsSection/DetailsSectionSkeleton'
import { FigsScrubsAreSection } from '../FigsScrubsAreSection/FigsScrubsAreSection'
import { PaginationDots } from '../GalleryImageCarousel/PaginationDots'
import { PaginationDotsWrapper } from '../GalleryImageCarousel/styles'
import { WhyWeLoveSkeleton } from '../WhyWeLoveSection/WhyWeLoveSkeleton'

import {
  Accordion,
  AccordionSection,
  Body,
  GalleryImageCarousel,
  PrimaryContent,
} from './ProductPageSkeleton.styles'

/**
 * The wrapper for the skeleton screen.
 */
export const Wrapper: React.FC<{
  children?: React.ReactNode
  className?: string
}> = ({ children, className }) => <Body className={className}>{children}</Body>

/**
 * The primary content section for the skeleton screen.
 * This should include the gallery and the details section (i.e. Everything above the sections below the gallery, details, and attribute selectors)
 */
export { PrimaryContent }

/**
 * The skeleton screen for the gallery swiper.
 * Should be included inside the `PrimaryContent` wrapper.
 */
export const Gallery = ({ withFitModelCaption = false }) => (
  <>
    <Media lessThan='md'>
      <GalleryImageCarousel>
        <PaginationDotsWrapper>
          <PaginationDots currentIndex={0} elementCount={6} />
        </PaginationDotsWrapper>
      </GalleryImageCarousel>
      {withFitModelCaption && (
        <FitModelCaption variant='model-selector-only'>
          <Skeleton height='13px' />
        </FitModelCaption>
      )}
    </Media>
    <Media greaterThanOrEqual='md'>
      <CarouselImageSectionSkeleton />
    </Media>
  </>
)

/**
 * The details column skeleton for the gallery swiper.
 * This includes the skeleton UI for product details, attribute selectors, and accordion sections.
 * Should be included inside the `PrimaryContent` wrapper.
 */
export const Details = () => (
  <>
    <Media lessThan='md'>
      <PageWidthContainer>
        <Spacer height={30} />
        <Skeleton height='17px' />
        <Spacer height={15} />
        <Skeleton height='15px' width={'calc(80 / 328 * 100%)'} />
        <Spacer height={46} />
        <Skeleton height='10px' width={'calc(140 / 328 * 100%)'} />
        <Spacer height={18} />
        <Skeleton height='40px' width={'calc(100% + 24px)'} />
        <Spacer height={30} />
        <Skeleton height='12px' />
        <Spacer height={16} />
        <Skeleton height='40px' width={'calc(100% + 24px)'} />
        <Spacer height={49} />
        <Skeleton height='1px' />
        <Spacer height={23} />
        <Skeleton height='35px' />
        <Spacer height={23} />
        <Skeleton height='51px' />
        <Spacer height={22} />
        <Skeleton height='19px' />
        <Spacer height={30} />
        <Skeleton height='11px' />
        <Spacer height={27} />
      </PageWidthContainer>
      <Accordion>
        <AccordionSection>
          <Skeleton height='12px' />
        </AccordionSection>
        <AccordionSection>
          <Skeleton height='12px' />
        </AccordionSection>
        <AccordionSection>
          <Skeleton height='13px' />
        </AccordionSection>
        <AccordionSection>
          <Skeleton height='10px' />
          <Spacer height={26} />
          <Skeleton height='32px' />
          <Spacer height={8} />
          <Skeleton height='32px' />
        </AccordionSection>
      </Accordion>
    </Media>
    <Media greaterThanOrEqual='md'>
      <DetailsSectionSkeleton />
    </Media>
  </>
)

/**
 * Skeleton for the "Why We Love" section
 */
export const WhyWeLove = () => (
  <>
    <Media greaterThanOrEqual='md'>
      <Spacer height={64} />
    </Media>
    <WhyWeLoveSkeleton />
  </>
)

/**
 * Skeleton for the "Figs Scrubs Are" section
 */
export const FigsScrubsAre = () => <FigsScrubsAreSection />
